import { Invoice } from '@/models/accounting/Invoice'
import { Document } from '@/models/Document'
import { createModelFromResponse } from '@/utils/model'
import axios from 'axios'

/**
 * Raw attachment bytes above which the API mails a download link instead of attachments.
 * Mirrors the MAIL_ATTACHMENT_BUDGET_BYTES default of the API (config mail.attachment_fallback.budget_bytes).
 */
export const INVOICE_MAIL_ATTACHMENT_BUDGET_BYTES = 7_000_000

export function totalMailSize(documents: Array<Document> | null | undefined): number {
	return (documents ?? []).reduce((total, document) => total + document.mailSize, 0)
}

export function exceedsMailAttachmentBudget(documents: Array<Document> | null | undefined): boolean {
	return totalMailSize(documents) > INVOICE_MAIL_ATTACHMENT_BUDGET_BYTES
}

export interface GenerateInvoiceData {
	attachedDocuments: Array<Document> | null
	invoiceNumber: number | null
	invoiceDate: string | null
	hasInvoiceNumber: boolean
	selectedLanguage: string | null
	combinedAttachments: boolean
}

export interface GenerateInvoiceResult {
	response: any
	pdfDocument: Document
	documentId: string
}

export async function generateInvoicePdf(
	item: Invoice,
	receivedData: GenerateInvoiceData | null
): Promise<GenerateInvoiceResult> {
	const requestBody = {
		data: {
			attributes: {
				invoiceDate: receivedData?.invoiceDate ?? null,
				...(receivedData?.hasInvoiceNumber
					? {}
					: {
							invoiceNumber: receivedData?.invoiceNumber ?? null
					  }),
				combinedAttachments: receivedData?.combinedAttachments ?? false
			}
		}
	}

	const response = await item.doCallbackWithLanguage(
		async (httpClient) => {
			return httpClient.post(`${item.getJsonApiType()}/${item.getApiId()}/generate`, requestBody)
		},
		receivedData?.selectedLanguage
	)

	const responseDocument = response.data.included?.find((r: any) => r.type === 'documents')
	if (!responseDocument) {
		throw new Error('No document in generate response')
	}

	const pdfDocument = createModelFromResponse(Document, { data: responseDocument }) as Document

	await axios.patch(`${pdfDocument.getModelUrl()}/attachments`, {
		data: (receivedData?.attachedDocuments ?? []).map((e) => e.toResourceLinkObject())
	})

	return {
		response,
		pdfDocument,
		documentId: responseDocument.id as string
	}
}
